import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import { useForm, UseFormReturn } from "react-hook-form";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { DevolutivaForm } from "@/components/access-request/form";
import type { TimelineEvent } from "@/@types/access-request.types";
import type { IUnlockDetails, UseApproveUnlock } from "@/@types/unlock-request";
import { HistoricoTimeline } from "@/components/access-request/timeline";
import { UserCard } from "@/components/access-request/user-card";
import {
  FormData,
  FormSchema,
} from "@/components/access-request/validate-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SITUATION_STATUS } from "@/constants/access-request";
import {
  UNLOCK_DETAIL_LABELS,
  UNLOCK_ENTITY_LABEL,
  UNLOCK_TAB_LABELS,
} from "@/constants/messages";
import {
  UNLOCK_APPROVE_TEST_ID,
  UNLOCK_IN_PROGRESS_TEST_ID,
  UNLOCK_TARGET_CARD_TEST_ID,
} from "@/constants/unlock-request";
import { StatusEnumAccessRequest } from "@/enum/access_request";
import { AccessRequestDTO } from "@/services/dtos/accessRequest.dto";
import { readWasBlocked, toTimelineEvent } from "@/utils/unlock-request";

import { readUnlockDetails } from "./access-details";
import { ApproveUnlockDialog } from "./approve-unlock-dialog";
import { SituationCell } from "./situation-cell";
import { useApproveUnlock } from "./use-approve-unlock";

interface UnlockDetailProps {
  request: AccessRequestDTO;
  backPath: string;
  canDecide: boolean;
}

export default function UnlockDetail({
  request,
  backPath,
  canDecide,
}: UnlockDetailProps): JSX.Element {
  const navigate: NavigateFunction = useNavigate();
  const formMethods: UseFormReturn<FormData> = useForm<FormData>({
    resolver: zodResolver(FormSchema),
  });
  const approval: UseApproveUnlock = useApproveUnlock(request, backPath);

  const unlock: IUnlockDetails | null = readUnlockDetails(request);
  const entityLabel: string = unlock
    ? (UNLOCK_ENTITY_LABEL[unlock.entityType] ?? UNLOCK_DETAIL_LABELS.UNKNOWN)
    : UNLOCK_DETAIL_LABELS.UNKNOWN;
  const entityCode: string = unlock?.entityCode ?? UNLOCK_DETAIL_LABELS.UNKNOWN;
  const storeCode: string = String(unlock?.storeCode ?? request.store);

  const timelineEvents: TimelineEvent[] = (request.history ?? []).map(
    toTimelineEvent,
  );

  const isPending: boolean = request.status === StatusEnumAccessRequest.PENDING;
  const isInProgress: boolean =
    request.status === StatusEnumAccessRequest.APPROVED;
  const canAct: boolean = canDecide && isPending;

  return (
    <div className="flex flex-col min-h-screen w-full">
      <div className="flex min-h-screen w-full flex-col bg-muted/40">
        <div className="flex flex-col sm:gap-4 sm:py-4">
          <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
            <div className="mx-auto grid w-full flex-1 auto-rows-max gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => navigate(backPath)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="sr-only">{UNLOCK_DETAIL_LABELS.BACK}</span>
                </Button>
                <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
                  {UNLOCK_DETAIL_LABELS.TITLE}
                </h1>
                <Badge variant="outline" className="ml-auto sm:ml-0">
                  {UNLOCK_TAB_LABELS.STORE(request.store)}
                </Badge>
                <div className="flex w-full items-center gap-2 sm:w-auto md:ml-auto">
                  {canAct ? (
                    <Button
                      size="sm"
                      className="w-full sm:w-auto"
                      data-testid={UNLOCK_APPROVE_TEST_ID}
                      disabled={approval.isApproving}
                      onClick={approval.openConfirm}
                    >
                      {UNLOCK_DETAIL_LABELS.APPROVE(entityLabel)}
                    </Button>
                  ) : null}
                  {isInProgress ? (
                    <Badge
                      variant="pending"
                      data-testid={UNLOCK_IN_PROGRESS_TEST_ID}
                    >
                      {UNLOCK_DETAIL_LABELS.IN_PROGRESS}
                    </Badge>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-[1fr_250px] lg:grid-cols-3 lg:gap-8">
                <div className="grid auto-rows-max items-start gap-4 lg:col-span-2 lg:gap-8">
                  <UserCard formMethods={formMethods} cpf={request.cpf} />

                  <Card data-testid={UNLOCK_TARGET_CARD_TEST_ID}>
                    <CardHeader>
                      <CardTitle>{UNLOCK_DETAIL_LABELS.TARGET_TITLE}</CardTitle>
                      <CardDescription>
                        {UNLOCK_DETAIL_LABELS.TARGET_DESCRIPTION}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-4 sm:grid-cols-2">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-muted-foreground">
                          {UNLOCK_DETAIL_LABELS.TYPE}
                        </span>
                        <span className="text-sm font-medium">
                          {entityLabel}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-muted-foreground">
                          {UNLOCK_DETAIL_LABELS.CODE}
                        </span>
                        <span className="text-sm font-medium">{entityCode}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-muted-foreground">
                          {UNLOCK_DETAIL_LABELS.STORE}
                        </span>
                        <span className="text-sm font-medium">{storeCode}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-muted-foreground">
                          {UNLOCK_DETAIL_LABELS.WAS_BLOCKED}
                        </span>
                        <span className="text-sm font-medium">
                          {readWasBlocked(unlock)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 sm:col-span-2">
                        <span className="text-sm text-muted-foreground">
                          {UNLOCK_DETAIL_LABELS.SITUATION_AT_REQUEST}
                        </span>
                        <div>
                          <SituationCell
                            situation={unlock?.seniorSituation ?? null}
                            situationStatus={
                              unlock?.seniorSituationStatus ??
                              SITUATION_STATUS.UNAVAILABLE
                            }
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {canAct ? <DevolutivaForm redirectPath={backPath} /> : null}
                </div>

                <div className="grid auto-rows-max items-start gap-4 lg:gap-8">
                  <HistoricoTimeline event={timelineEvents} />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ApproveUnlockDialog
        approval={approval}
        description={UNLOCK_DETAIL_LABELS.CONFIRM_DESCRIPTION(
          entityLabel,
          request.name,
          entityCode,
          storeCode,
        )}
      />
    </div>
  );
}
